import React, { FC, useCallback, useMemo } from "react";
import { getResultColumns, Grid } from "@getregistered/greg-editors";
import {
  gridLoader,
  GridLoadSuccessResponse,
} from "../../../domain/grid/resultLoader";
import { PaginatedApplicationPrimerGrid, AsyncDownloadLink } from "../PaginatedApplicationPrimerGrid";
import { saveReport } from "./saveReport";
import { renderCheckinCellPrimer } from "../CheckinCell/renderCheckinCellPrimer";
import { DEFAULT_PER_PAGE, DEFAULT_PER_PAGE_STORAGE_KEY } from "../constants";
import { StaleFiltersModalPrimer } from "./StaleFiltersModalPrimer";
import { useStaleFilters } from "./useStaleFilters";

interface ReportGridResponse extends GridLoadSuccessResponse {
  staleFilters?: string[];
}

export interface PaginatedReportPrimerGridProps {
  grid: Grid;
  editable: boolean;
  reportUrl: string;
  totalsUrl: string;
  canExport?: boolean;
  // For async exports
  eventId?: string;
  reportId?: number;
  reportExportsUrl?: string;
  // Grid type the export job dispatches on; org-level cross-event reports use
  // "event_report", event-scoped reports default to "report".
  gridType?: string;
}

export const PaginatedReportPrimerGrid: FC<PaginatedReportPrimerGridProps> = ({
  grid,
  editable,
  reportUrl,
  totalsUrl,
  canExport,
  eventId,
  reportId,
  reportExportsUrl,
  gridType = "report",
}) => {
  const { showStaleFiltersModal, setShowStaleFiltersModal } = useStaleFilters(reportId);

  const handleFetchResults = useCallback((response: ReportGridResponse) => {
    if (response.staleFilters && response.staleFilters.length > 0) {
      setShowStaleFiltersModal(true);
    }
  }, [setShowStaleFiltersModal]);

  const columns = useMemo(
    () => getResultColumns(grid.columns),
    [grid.columns]
  );

  const loader = useMemo(
    () =>
      gridLoader<ReportGridResponse>(
        reportUrl,
        totalsUrl,
        columns,
        handleFetchResults
      ),
    [reportUrl, totalsUrl, columns, handleFetchResults]
  );
  const saveGrid = (update: Partial<Grid>) => saveReport(reportUrl, update);

  const processedGrid = editable
    ? grid
    : {
        ...grid,
        filters: undefined,
        fixedFilters: grid.filters,
      };

  // GR-2497: downloads are async-only. Sync csvUrl/xlsxUrl routes 406 since
  // GR-1915/GR-1916, so the grid no longer receives or falls back to them.
  // Event-scoped reports also pass eventId; org-level cross-event reports
  // don't need one (it only seeds the client-side dedup key).
  const asyncDownloadLinks: AsyncDownloadLink[] = [];

  if (canExport && reportExportsUrl && reportId) {
    for (const exportType of ["csv", "xlsx"] as const) {
      asyncDownloadLinks.push({
        title: `Download ${exportType.toUpperCase()}`,
        eventId,
        gridType,
        exportType,
        reportId,
        reportExportsUrl,
      });
    }
  }

  return (
    <>
      <StaleFiltersModalPrimer isOpen={showStaleFiltersModal} />
      <PaginatedApplicationPrimerGrid
        grid={processedGrid}
        defaultPerPage={DEFAULT_PER_PAGE}
        perPageStorageKey={DEFAULT_PER_PAGE_STORAGE_KEY}
        page={1}
        loader={loader}
        onSave={editable ? saveGrid : undefined}
        renderCell={renderCheckinCellPrimer as any}
        showActions={false}
        actionCellRenderer="menu"
        asyncDownloadLinks={asyncDownloadLinks.length > 0 ? asyncDownloadLinks : undefined}
      />
    </>
  );
};
